/**
 * ManualServiceCard - Card for manually-tracked services
 *
 * Displays plan, cost, expiration, notes. Click edit to open modal.
 */

import { Button, Typography, Descriptions } from "antd";
import ServiceCard from "./ServiceCard";
import type { ManualService } from "@/services/subscriptionsService";
import {
  getDaysUntilExpiration,
  getExpirationAlertLevel,
} from "@/services/subscriptionsService";
import { MaterialIcon } from "@/components/MaterialIcon";

const { Text } = Typography;

interface ManualServiceCardProps {
  service: ManualService;
  onEdit: (service: ManualService) => void;
}

export default function ManualServiceCard({ service, onEdit }: ManualServiceCardProps) {
  const daysLeft = getDaysUntilExpiration(service.expirationDate);
  const alertLevel = daysLeft != null ? getExpirationAlertLevel(daysLeft) : "ok";

  const editButton = (
    <Button
      size="small"
      icon={<MaterialIcon name="edit" size={14} />}
      onClick={() => onEdit(service)}
    />
  );

  return (
    <ServiceCard
      title={service.name}
      alertLevel={alertLevel}
      deepLink={service.deepLink}
      extra={editButton}
    >
      <Descriptions size="small" column={2} colon={false}>
        {service.plan && (
          <Descriptions.Item label="Plan">{service.plan}</Descriptions.Item>
        )}
        {service.cost && (
          <Descriptions.Item label="Cost">{service.cost}</Descriptions.Item>
        )}
        {service.expirationDate && (
          <Descriptions.Item label="Expires">
            {service.expirationDate}
            {daysLeft != null && (
              <Text
                type={alertLevel === "critical" ? "danger" : alertLevel === "warning" ? "warning" : "secondary"}
                style={{ marginLeft: 8 }}
              >
                ({daysLeft}d)
              </Text>
            )}
          </Descriptions.Item>
        )}
        {service.notes && (
          <Descriptions.Item label="Notes" span={2}>{service.notes}</Descriptions.Item>
        )}
      </Descriptions>
      {service.updatedAt && (
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: "block" }}>
          Last updated: {service.updatedAt.toDate().toLocaleString()}
        </Text>
      )}
    </ServiceCard>
  );
}
